import React, { useState, useEffect } from 'react';
import { getDataBS, deleteDataBS } from '../../utils/awsService';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';

const DepressionTestPanel = () => {
  const [depressionTests, setDepressionTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDepressionTests = async () => {
      try {
        setLoading(true);
        setError(null);

        const responseData = await getDataBS('/depressiontest');

        // Add null checks for the response structure
        if (!responseData) {
          throw new Error('No response data received');
        }

        // Check if data exists and has the expected structure
        if (!responseData.data || !responseData.data.data) {
          throw new Error('Invalid response structure');
        }

        // Extract depression test data with additional null checks
        let depressionTestData = responseData.data.data;

        // Ensure depressionTestData is an array
        if (!Array.isArray(depressionTestData)) {
          throw new Error('Expected array of depression tests');
        }

        // Transform data to extract specific fields with comprehensive null checks
        const processedDepressionTests = depressionTestData.map((test, index) => {
          // Handle case where test might be null/undefined
          if (!test || typeof test !== 'object') {
            console.warn(`Invalid test data at index ${index}:`, test);
            return null;
          }

          return {
            id: test.id || `temp-id-${index}`, // Provide fallback ID
            fullName: test.fullName || 'Anonymous',
            email: test.email || '',
            phone: test.contactNumber || '',
            submissionDate: test.submissionDate || new Date().toISOString(),
            score: typeof test.score === 'number' ? test.score : 0,
            severity: getSeverityLabel(typeof test.score === 'number' ? test.score : 0)
          };
        }).filter(test => test !== null); // Remove any null entries

        // Sort based on submission date
        const sortedTests = processedDepressionTests.sort((a, b) => {
          try {
            const dateA = new Date(a.submissionDate);
            const dateB = new Date(b.submissionDate);
            
            // Check if dates are valid
            if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
              return 0; // Keep original order if dates are invalid
            }
            
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
          } catch (sortError) {
            console.warn('Error sorting dates:', sortError);
            return 0;
          }
        });

        setDepressionTests(sortedTests);
        setFilteredTests(sortedTests);
      } catch (error) {
        console.error('Error fetching depression tests:', error);
        setError(error.message || 'Failed to fetch depression tests');
        setDepressionTests([]);
        setFilteredTests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDepressionTests();
    const intervalId = setInterval(fetchDepressionTests, 30000);
    
    return () => clearInterval(intervalId);
  }, [sortOrder]);

  // Filter tests based on search query
  useEffect(() => {
    if (!searchQuery || !searchQuery.trim()) {
      setFilteredTests(depressionTests);
      return;
    }

    try {
      const query = searchQuery.toLowerCase();
      const filtered = depressionTests.filter(test => {
        // Add null checks for all searchable fields
        if (!test) return false;
        
        const fullName = test.fullName ? test.fullName.toLowerCase() : '';
        const email = test.email ? test.email.toLowerCase() : '';
        const phone = test.phone ? test.phone.toString() : '';
        
        return fullName.includes(query) || 
               phone.includes(query) || 
               email.includes(query);
      });
      
      setFilteredTests(filtered);
    } catch (filterError) {
      console.error('Error filtering tests:', filterError);
      setFilteredTests(depressionTests); // Fallback to unfiltered data
    }
  }, [searchQuery, depressionTests]);

  // Helper function to determine severity label based on score
  const getSeverityLabel = (score) => {
    // Ensure score is a number
    const numScore = typeof score === 'number' ? score : 0;
    
    if (numScore <= 4) return 'Minimal';
    if (numScore <= 9) return 'Mild';
    if (numScore <= 14) return 'Moderate';
    if (numScore <= 19) return 'Moderately Severe';
    return 'Severe';
  };

  const toggleSortOrder = () => {
    setSortOrder(prevOrder => prevOrder === 'newest' ? 'oldest' : 'newest');
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown Date';
      
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch (dateError) {
      console.warn('Error formatting date:', dateError);
      return 'Unknown Date';
    }
  };

  const handleSearch = (e) => {
    if (e && e.target) {
      setSearchQuery(e.target.value);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  if (loading) {
    return <div className="loading-container">Loading depression test submissions...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const handleView = (id) => {
    if (id) {
      navigate(`/admin/responses/depression-test/${id}`);
    } else {
      console.error('Cannot view test: No ID provided');
      alert('Error: Cannot view test details');
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      alert('Error: Cannot delete test - no ID provided');
      return;
    }

    if (window.confirm('Are you sure you want to delete this depression test submission?')) {
      try {
        await deleteDataBS('/depressiontest', id);
        
        setDepressionTests(prevTests => 
          prevTests.filter(test => test && test.id !== id)
        );
      } catch (error) {
        console.error('Error deleting depression test:', error);
        alert('Failed to delete depression test.');
      }
    }
  };

  const handleBack = () => {
    navigate('/admin/admin-dashboard');
  };

  // Function to determine card color based on severity
  const getSeverityColor = (severity) => {
    if (!severity) return '';
    
    switch (severity) {
      case 'Minimal': return 'severity-minimal';
      case 'Mild': return 'severity-mild';
      case 'Moderate': return 'severity-moderate';
      case 'Moderately Severe': return 'severity-moderately-severe';
      case 'Severe': return 'severity-severe';
      default: return '';
    }
  };

  return (
    <div className="admin-container">
      <button onClick={handleBack} className="back-btn">← Back to Dashboard</button>

      <h1 className="admin-title">Depression Test Submissions Admin Panel</h1>
      
      <div className="controls-container">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by name, email or phone"
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
          />
          {searchQuery && (
            <button onClick={clearSearch} className="clear-search-btn">
              Clear
            </button>
          )}
        </div>
        
      </div>
      
      {filteredTests.length === 0 ? (
        <div className="no-data-container">
          {searchQuery ? (
            <p className="no-data">No results match your search.</p>
          ) : (
            <p className="no-data">No depression test submissions found.</p>
          )}
          <button onClick={() => window.location.reload()} className="retry-btn">
            Reload Data
          </button>
        </div>
      ) : (
        <div className="consultations-grid">
          {filteredTests.map((test) => {
            // Additional safety check before rendering
            if (!test) return null;
            
            return (
              <div key={test.id || Math.random()} className={`consultation-card ${getSeverityColor(test.severity)}`}>
                <div className="card-header">
                  <h3 className="client-name">{test.fullName || 'Anonymous'}</h3>
                </div>
                <div className="card-content">
                  <p>Email: {test.email || 'N/A'}</p>
                  {test.phone && <p>Phone: {test.phone}</p>}
                  <p>Submission Date: {formatDate(test.submissionDate)}</p>
                  <p>Score: {test.score || 0} - <span className="severity-label">{test.severity || 'Unknown'}</span></p>
                </div>
                <div className="card-actions">
                  <button 
                    onClick={() => handleView(test.id)}
                    className="view-btn"
                    disabled={!test.id}
                  >
                    View Details
                  </button>
                  <button 
                    onClick={() => handleDelete(test.id)}
                    className="delete-btn"
                    disabled={!test.id}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DepressionTestPanel; 